import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Select, Spin, Table, Typography } from "antd";
import { API_BASE } from "./config/api";
import { getClientToken, handleClientAuthFailure } from "./config/auth";

const authConfig = () => ({ headers: { "X-KG-Client-Authorization": `Bearer ${getClientToken()}` } });
const money = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const date = (value) => {
  if (!value) return "-";
  const [y, m, d] = String(value).split("-");
  return y && m && d ? `${d}-${m}-${y}` : value;
};
const monthlyActiveValue = (r) => {
  if (String(r?.sip_status || "").toUpperCase() !== "RUNNING") return 0;
  const amount = Number(r?.sip_amount || 0);
  const frequency = String(r?.frequency || "").toUpperCase();
  if (frequency === "DAILY") return amount * 22;
  if (frequency === "WEEKLY") return amount * 4;
  if (frequency === "MONTHLY") return amount;
  return 0;
};
const monthlyPausedValue = (r) => {
  if (String(r?.sip_status || "").toUpperCase() !== "PAUSED") return 0;
  const amount = Number(r?.sip_amount || 0);
  const frequency = String(r?.frequency || "").toUpperCase();
  if (frequency === "DAILY") return amount * 22;
  if (frequency === "WEEKLY") return amount * 4;
  if (frequency === "MONTHLY") return amount;
  return 0;
};

const monthlySipValue = (r) => {
  const amount = Number(r?.sip_amount || 0);
  const frequency = String(r?.frequency || "").toUpperCase();
  if (frequency === "DAILY") return amount * 22;
  if (frequency === "WEEKLY") return amount * 4;
  if (frequency === "MONTHLY") return amount;
  return 0;
};

const sipStatusLabel = (value) => {
  const status = String(value || "").toUpperCase();
  if (status === "RUNNING" || status === "ACTIVE") return "Active";
  if (status === "PAUSED" || status === "PAUSE") return "Paused";
  return status ? "Ceased" : "—";
};
const sipDayLabel = (r) => {
  const frequency = String(r?.frequency || "").toUpperCase();
  if (frequency === "WEEKLY" || frequency === "MONTHLY") return r?.installment_day || "—";
  return "—";
};
const weeklyDayOrder = { MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6, SUNDAY: 7 };
const sipDisplaySort = (a, b) => {
  const fa = String(a?.frequency || "").toUpperCase();
  const fb = String(b?.frequency || "").toUpperCase();
  const rank = (f) => f === "MONTHLY" ? 1 : f === "WEEKLY" ? 2 : f === "DAILY" ? 3 : 4;
  const rankDiff = rank(fa) - rank(fb);
  if (rankDiff) return rankDiff;
  if (fa === "MONTHLY") {
    const da = Number.parseInt(a?.installment_day, 10);
    const db = Number.parseInt(b?.installment_day, 10);
    return (Number.isFinite(da) ? da : 99) - (Number.isFinite(db) ? db : 99);
  }
  if (fa === "WEEKLY") {
    const da = weeklyDayOrder[String(a?.installment_day || "").toUpperCase()] || 99;
    const db = weeklyDayOrder[String(b?.installment_day || "").toUpperCase()] || 99;
    return da - db;
  }
  return 0;
};

export default function ClientSips() {
  const [contextLoading, setContextLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isFamily, setIsFamily] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedPan, setSelectedPan] = useState("ALL");
  const [sipType, setSipType] = useState("active");
  const [rows, setRows] = useState([]);
  const [activeRows, setActiveRows] = useState([]);
  const [pausedRows, setPausedRows] = useState([]);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/get_client_dashboard_summary.php`, authConfig());
        if (r.data?.status !== "ok") throw new Error(r.data?.message || "Unable to load client context.");
        const family = Boolean(r.data?.is_family);
        const list = Array.isArray(r.data?.members) ? r.data.members : [];
        setIsFamily(family);
        setMembers(list);
        setSelectedPan(family ? "ALL" : (r.data?.portfolio?.dashboard_pan || "ALL"));
      } catch (e) {
        if (handleClientAuthFailure(e.response, e.response?.data)) return;
        setError(e.response?.data?.message || e.message || "Unable to load client context.");
      } finally { setContextLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (contextLoading || error) return;
    (async () => {
      setLoading(true); setError("");
      try {
        const params = { mode: "detailed", pan: isFamily ? selectedPan : undefined };
        const displayPromise = axios.get(`${API_BASE}/get_client_sip.php`, { ...authConfig(), params: { ...params, status: sipType } });
        const activePromise = sipType === "active" ? displayPromise : axios.get(`${API_BASE}/get_client_sip.php`, { ...authConfig(), params: { ...params, status: "active" } });
        const pausedPromise = sipType === "paused" ? displayPromise : axios.get(`${API_BASE}/get_client_sip.php`, { ...authConfig(), params: { ...params, status: "paused" } });
        const [displayRes, activeRes, pausedRes] = await Promise.all([displayPromise, activePromise, pausedPromise]);
        if (displayRes.data?.status !== "ok") throw new Error(displayRes.data?.message || "Unable to load SIPs.");
        if (activeRes.data?.status !== "ok") throw new Error(activeRes.data?.message || "Unable to load active SIP total.");
        if (pausedRes.data?.status !== "ok") throw new Error(pausedRes.data?.message || "Unable to load paused SIP total.");
        setRows(Array.isArray(displayRes.data?.data) ? displayRes.data.data : []);
        setActiveRows(Array.isArray(activeRes.data?.data) ? activeRes.data.data : []);
        setPausedRows(Array.isArray(pausedRes.data?.data) ? pausedRes.data.data : []);
      } catch (e) {
        if (handleClientAuthFailure(e.response, e.response?.data)) return;
        setRows([]); setActiveRows([]); setPausedRows([]);
        setError(e.response?.data?.message || e.message || "Unable to load SIPs.");
      } finally { setLoading(false); }
    })();
  }, [contextLoading, isFamily, selectedPan, sipType]);

  const panOptions = useMemo(() => [
    { value: "ALL", label: "ALL" },
    ...members.map((m) => ({ value: m.pan, label: `${m.invester_name || m.child_name || "Client"} - ${m.pan}` })),
  ], [members]);

  const memberMap = useMemo(() => {
    const map = {};
    members.forEach((m) => { if (m.pan) map[m.pan] = m.invester_name || m.child_name || m.pan; });
    return map;
  }, [members]);

  const activeTotals = useMemo(() => {
    const byPan = {};
    let all = 0;
    activeRows.forEach((r) => {
      const v = monthlyActiveValue(r);
      all += v;
      byPan[r.pan] = (byPan[r.pan] || 0) + v;
    });
    return { all, byPan };
  }, [activeRows]);

  const pausedTotals = useMemo(() => {
    const byPan = {};
    let all = 0;
    pausedRows.forEach((r) => {
      const v = monthlyPausedValue(r);
      all += v;
      byPan[r.pan] = (byPan[r.pan] || 0) + v;
    });
    return { all, byPan };
  }, [pausedRows]);

  const groups = useMemo(() => {
    if (!(isFamily && selectedPan === "ALL")) return [{ pan: selectedPan, rows: [...rows].sort(sipDisplaySort) }];
    const grouped = {};
    rows.forEach((r) => { (grouped[r.pan] ||= []).push(r); });
    return Object.entries(grouped).map(([pan, data]) => ({ pan, rows: [...data].sort(sipDisplaySort) }));
  }, [rows, isFamily, selectedPan]);

  const columns = useMemo(() => {
    const cols = [
      { title: "Folio", dataIndex: "folio_number", width: "18%" },
      { title: "Scheme", dataIndex: "scheme_name", width: "38%", ellipsis: false, onCell: () => ({ className: "kg-mobile-full-name-cell" }), render: (v) => <span className="kg-mobile-full-name" title={v}>{v || "-"}</span> },
      { title: "SIP Amount", dataIndex: "sip_amount", width: "16%", align: "right", render: (v) => `₹${money(v)}` },
      { title: "Frequency", dataIndex: "frequency", width: "14%" },
      { title: "SIP Day", dataIndex: "installment_day", width: "14%", render: (v) => v || "-" },
    ];
    if (sipType === "all") cols.push({ title: "Status", dataIndex: "sip_status", width: "12%", render: (v) => v === "RUNNING" ? "Active" : v === "PAUSED" ? "Paused" : "Ceased" });
    return cols;
  }, [sipType]);

  if (contextLoading) return <div style={{ padding: 30, textAlign: "center" }}><Spin /></div>;

  return (
    <div className="kg-sips-page">
      <style>{`
        .kg-sips-page { width:100%; padding:4px 14px 24px; background:transparent; }
        .kg-sips-controls { display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; margin-bottom:14px; }
        .kg-sips-table .ant-table { background:transparent; font-size:${Math.max(10, 12 * zoom / 100)}px; }
        .kg-sips-table .ant-table-container, .kg-sips-table .ant-table-content { overflow-x:visible !important; }
        .kg-sips-table table { width:100% !important; table-layout:fixed !important; border-collapse:collapse !important; border:1px solid #000 !important; }
        .kg-sips-table th, .kg-sips-table td { white-space:nowrap !important; padding:${Math.max(4, 7 * zoom / 100)}px ${Math.max(4, 7 * zoom / 100)}px !important; overflow:hidden; text-overflow:ellipsis; border:1px solid #000 !important; }
        .kg-sips-table .ant-table-thead > tr > th { background:transparent !important; box-shadow:none !important; border-radius:0 !important; border-top:1px solid #000 !important; border-bottom:1px solid #000 !important; }
        .kg-sips-table .ant-table-thead > tr > th::before { display:none !important; }
        .kg-sips-section { margin-top:18px; }
        .kg-sips-section-head { display:flex; justify-content:space-between; gap:10px; align-items:baseline; border-bottom:1px solid #e8e8e8; padding:0 10px 8px; margin-bottom:8px; white-space:nowrap; }
        .kg-sips-total { font-weight:700; white-space:nowrap; }
        .kg-sips-cards { display:none; }
        @media (max-width: 700px) {
          .kg-sips-table { display:none !important; }
          .kg-sips-cards { display:grid; gap:10px; width:100%; }
          .kg-sip-card { background:#f3f8ff; border:1px solid #dbe5ef; border-radius:16px; padding:13px 14px; box-shadow:0 5px 16px rgba(21,64,103,.08); }
          .kg-sip-card:nth-child(even) { background:#fff9e9; }
          .kg-sip-scheme { color:#123f6d; font-size:16px; font-weight:800; line-height:1.28; margin-bottom:5px; overflow-wrap:anywhere; }
          .kg-sip-meta { display:flex; justify-content:space-between; gap:10px; align-items:baseline; margin-bottom:10px; }
          .kg-sip-amc { color:#657586; font-size:12px; font-weight:650; line-height:1.3; min-width:0; overflow-wrap:anywhere; }
          .kg-sip-folio { color:#44576a; font-size:12px; font-weight:700; line-height:1.3; flex:0 0 auto; text-align:right; }
          .kg-sip-row3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; padding-bottom:10px; border-bottom:1px solid #e8eef4; }
          .kg-sip-field { min-width:0; }
          .kg-sip-field.center { text-align:center; }
          .kg-sip-field.right { text-align:right; }
          .kg-sip-label { color:#6c7885; font-size:11px; line-height:1.2; margin-bottom:3px; }
          .kg-sip-value { color:#172433; font-size:14px; font-weight:750; line-height:1.25; overflow-wrap:anywhere; }
          .kg-sip-amount { font-size:17px; color:#123f6d; }
          .kg-sip-detail { display:grid; grid-template-columns:92px minmax(0,1fr); gap:8px; align-items:baseline; padding-top:9px; }
          .kg-sip-detail + .kg-sip-detail { padding-top:7px; }
          .kg-sip-detail .kg-sip-label { margin:0; }
          .kg-sip-status { display:inline-flex; align-items:center; border-radius:999px; padding:3px 9px; font-size:12px; font-weight:800; }
          .kg-sip-status.active { color:#087b55; background:#e1f7ee; }
          .kg-sip-status.paused { color:#9a6500; background:#fff3d7; }
          .kg-sip-status.ceased { color:#b3263b; background:#fde8ec; }
          .kg-sips-page { width:100%; max-width:100%; padding:0 10px 18px; overflow-x:hidden; box-sizing:border-box; }
          .kg-sips-page .ant-typography { font-size:20px !important; line-height:1.2 !important; margin-bottom:10px !important; }
          .kg-sips-controls { width:100%; gap:6px; align-items:stretch; }
          .kg-sips-control { min-width:0 !important; flex:1 1 100% !important; width:100%; }
          .kg-sips-controls > div:last-child { margin-left:0 !important; width:100%; text-align:right; }
          .kg-sips-total { white-space:normal; overflow-wrap:anywhere; font-size:13px; line-height:1.35; font-weight:800; }
          .kg-sips-section { width:100%; max-width:100%; overflow:hidden; }
          .kg-sips-section-head { white-space:normal; flex-wrap:wrap; align-items:flex-start; font-size:14px; line-height:1.3; gap:4px 8px; padding-left:8px; padding-right:8px; }
          .kg-sips-section-head > strong { font-size:15px; }
          .kg-sips-table, .kg-sips-table .ant-spin-nested-loading, .kg-sips-table .ant-spin-container,
          .kg-sips-table .ant-table, .kg-sips-table .ant-table-container, .kg-sips-table .ant-table-content { width:100% !important; max-width:100% !important; overflow:hidden !important; }
          .kg-sips-table .ant-table { font-size:${Math.max(6, 8 * zoom / 100)}px; }
          .kg-sips-table table { width:100% !important; min-width:0 !important; table-layout:fixed !important; }
          .kg-sips-table th, .kg-sips-table td { min-width:0 !important; max-width:0; padding:2px 1px !important; line-height:1.15 !important; }
          .kg-sips-table th { white-space:normal !important; overflow-wrap:anywhere; word-break:break-word; text-align:center; }
          .kg-sips-table td { white-space:nowrap !important; }
          .kg-sips-table .ant-table-cell { box-sizing:border-box; }
        }
        @media (max-width: 420px) {
          .kg-sips-table .ant-table { font-size:${Math.max(6, 7 * zoom / 100)}px; }
          .kg-sips-table th, .kg-sips-table td { padding:2px 1px !important; }
        }
        @media (max-width: 700px) {
          .kg-mobile-full-name,
          .kg-mobile-full-name > span,
          .kg-mobile-full-name-cell,
          .kg-mobile-full-name-cell > span {
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            line-height: 1.15 !important;
            height: auto !important;
          }
          .kg-mobile-full-name-cell {
            font-size: 7px !important;
          }
        }
      `}
</style>

      <Typography.Title level={2} style={{ marginTop: 0, marginBottom: 12 }}>My SIPs</Typography.Title>
      <div className="kg-sips-controls">
        {isFamily && <div className="kg-sips-control" style={{ minWidth: 245 }}><div style={{ fontWeight: 600, marginBottom: 5 }}>SIP of</div><Select value={selectedPan} onChange={setSelectedPan} options={panOptions} style={{ width: "100%" }} /></div>}
        <div className="kg-sips-control" style={{ minWidth: 175 }}><div style={{ fontWeight: 600, marginBottom: 5 }}>SIP Type</div><Select value={sipType} onChange={setSipType} style={{ width: "100%" }} options={[{ value:"all", label:"All" }, { value:"active", label:"Active" }, { value:"ceased", label:"Ceased" },
              { value:"paused", label:"Paused" }]} /></div>
        <div style={{ marginLeft: "auto", whiteSpace: "nowrap" }}><Button size="small" onClick={() => setZoom((z) => Math.max(75, z - 10))}>−</Button><span style={{ display:"inline-block", minWidth:48, textAlign:"center", fontSize:12 }}>{zoom}%</span><Button size="small" onClick={() => setZoom((z) => Math.min(130, z + 10))}>+</Button></div>
      </div>

      <div className="kg-sips-total" style={{ marginBottom: 10 }}>
        {sipType === "active" && <>Monthly Active SIP Total: ₹{money(activeTotals.all)}</>}
        {sipType === "ceased" && <>Monthly Ceased SIP Total: ₹{money(rows.reduce((sum, r) => sum + monthlySipValue(r), 0))}</>}
        {sipType === "paused" && <>Monthly Paused SIP Total: ₹{money(pausedTotals.all)}</>}
        {sipType === "all" && <>Monthly Active SIP Total: ₹{money(activeTotals.all)}&nbsp;&nbsp; | &nbsp;&nbsp;Monthly Ceased SIP Total: ₹{money(rows.filter((r) => String(r?.sip_status || "").toUpperCase() === "CEASED").reduce((sum, r) => sum + monthlySipValue(r), 0))}&nbsp;&nbsp; | &nbsp;&nbsp;Monthly Paused SIP Total: ₹{money(pausedTotals.all)}</>}
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {!loading && rows.length === 0 ? (
          <div style={{ fontWeight: 700, padding: "12px 2px" }}>No SIP Found</div>
        ) : (
          groups.map((g) => (
            <div className="kg-sips-section" key={g.pan || "single"}>
              {isFamily && selectedPan === "ALL" && <div className="kg-sips-section-head"><strong className="kg-mobile-full-name">{memberMap[g.pan] || g.pan}</strong><span className="kg-sips-total">
                  {sipType === "active" && <>Monthly Active SIP Total: ₹{money(activeTotals.byPan[g.pan] || 0)}</>}
                  {sipType === "ceased" && <>Monthly Ceased SIP Total: ₹{money(g.rows.reduce((sum, r) => sum + monthlySipValue(r), 0))}</>}
                  {sipType === "paused" && <>Monthly Paused SIP Total: ₹{money(pausedTotals.byPan[g.pan] || 0)}</>}
                  {sipType === "all" && <>Monthly Active SIP Total: ₹{money(activeTotals.byPan[g.pan] || 0)}&nbsp;&nbsp; | &nbsp;&nbsp;Monthly Ceased SIP Total: ₹{money(g.rows.filter((r) => String(r?.sip_status || "").toUpperCase() === "CEASED").reduce((sum, r) => sum + monthlySipValue(r), 0))}&nbsp;&nbsp; | &nbsp;&nbsp;Monthly Paused SIP Total: ₹{money(pausedTotals.byPan[g.pan] || 0)}</>}
                </span></div>}
              <div className="kg-sips-cards">
                {g.rows.map((r, index) => {
                  const statusLabel = sipStatusLabel(r?.sip_status);
                  const statusClass = statusLabel.toLowerCase();
                  const bankName = r?.bank_name || r?.bank || "—";
                  const accountNumber = r?.account_number || r?.bank_account_number || r?.account_no || "—";
                  const amcName = r?.amc_name || r?.amc || r?.fund_house || r?.fund_house_name || "—";
                  const folioNumber = r?.folio_number || r?.folio || "—";
                  return (
                    <div className="kg-sip-card" key={r.id || `${r.pan}-${r.folio_number}-${r.scheme_code}-${r.sip_amount}-${r.frequency}-${index}`}>
                      <div className="kg-sip-scheme">{r?.scheme_name || "—"}</div>
                      <div className="kg-sip-meta">
                        <div className="kg-sip-amc">{amcName}</div>
                        <div className="kg-sip-folio">Folio: {folioNumber}</div>
                      </div>
                      <div className="kg-sip-row3">
                        <div className="kg-sip-field">
                          <div className="kg-sip-label">Amount</div>
                          <div className="kg-sip-value kg-sip-amount">₹{money(r?.sip_amount)}</div>
                        </div>
                        <div className="kg-sip-field center">
                          <div className="kg-sip-label">Type</div>
                          <div className="kg-sip-value">{r?.frequency || "—"}</div>
                        </div>
                        <div className="kg-sip-field right">
                          <div className="kg-sip-label">Day</div>
                          <div className="kg-sip-value">{sipDayLabel(r)}</div>
                        </div>
                      </div>
                      <div className="kg-sip-detail"><div className="kg-sip-label">Bank Name</div><div className="kg-sip-value">{bankName}</div></div>
                      <div className="kg-sip-detail"><div className="kg-sip-label">A/C Number</div><div className="kg-sip-value">{accountNumber}</div></div>
                      <div className="kg-sip-detail"><div className="kg-sip-label">Status</div><div><span className={`kg-sip-status ${statusClass}`}>{statusLabel}</span></div></div>
                    </div>
                  );
                })}
              </div>
              <Table className="kg-sips-table" rowKey={(r) => r.id || `${r.pan}-${r.folio_number}-${r.scheme_code}-${r.sip_amount}-${r.frequency}`} columns={columns} dataSource={g.rows} loading={false} pagination={false} size="small" />
            </div>
          ))
        )}
      </Spin>
    </div>
  );
}
